import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import ClaimStepContainer, { ClaimStepContainerProps } from './ClaimStepContainer';
import container from '../../../../container';
import QueryResult from '../../../common2/QueryResult';
import { ClaimContext } from '../types';
import { createClaimContextFromDraft } from '../../../claims-center/submit-claims/createClaimContext';
import { useClaimsShopSettings } from '../../ClaimsShopSettingsContext';

type Props = ClaimStepContainerProps & {
  shopId: number;
  claim: Pick<ClaimContext, 'draftId' | 'entryPoint'>;
  onChange: (data: Partial<ClaimContext>) => void;
  onDraftLoaded: (context: ClaimContext) => void;
};

function DraftLoadingStep(props: Props) {
  const { shopId, claim, onChange, onNext, onDraftLoaded } = props;
  const { draftId, entryPoint } = claim;

  const shopSettings = useClaimsShopSettings();

  const result = useQuery({
    queryKey: ['load-draft-and-requirements', draftId],
    queryFn: async () => {
      if (!draftId) {
        return null;
      }

      const draft = await container.touchpointsApi.getClaim(shopId, draftId);
      const claimRequirements = await container.touchpointsApi.postClaimRequirements(shopId, {
        carrierCode: draft.carrierCode,
        carrierCountryCode: draft.carrierCountryCode,
        deliveryDate: draft.deliveryDate,
        postalReturnDeliveryDate: draft.postalReturnDeliveryDate,
        deliveredToMailboxDate: draft.deliveredToMailboxDate,
        isReturnShipment: draft.isReturnShipment || false,
        isMultiShipmentOrder: draft.isMultiShipmentOrder,
      });

      return { draft, claimRequirements };
    },
  });

  useEffect(() => {
    if (result.isFetchedAfterMount && result.data && onNext) {
      const context = createClaimContextFromDraft(
        entryPoint,
        result.data.draft,
        shopSettings,
        result.data.claimRequirements
      );
      onChange(context);
      onDraftLoaded(context);
      onNext({ replace: true, context });
    }
  }, [entryPoint, result, onChange, onNext, onDraftLoaded, shopSettings]);

  return (
    <ClaimStepContainer {...props} onNext={null} onBack={null} onSaveDraft={null}>
      <QueryResult result={result} loadingText="CLAIM_DRAFT_LOADING" />
    </ClaimStepContainer>
  );
}

export default DraftLoadingStep;
